import React from 'react';

import { Box, Checkbox, Chip, FormControl, FormHelperText, InputLabel, ListItemText, MenuItem, Select } from '@material-ui/core';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import ensureIsArray from 'helpers/ensureIsArray';

import safeT from '../../helpers/safeT/safeT';

const FormSelect = ({ className, formik, id, label, disabled, options, variant, color, required, size, multiple }) => {
  const { t } = useTranslation();
  const { touched, errors, handleChange, values } = formik;
  const value = get(values, id);
  const error = get(errors, id);
  const isTouched = get(touched, id);

  const setTouched = () => {
    formik.setFieldTouched(id, true);
  };

  const unifiedLabel = required ? `${label} * ` : label;

  const truncateLabel = labelText => {
    if (labelText.length > 15) {
      return `${labelText.substring(0, 15)}...`;
    }
    return labelText;
  };

  return (
    <FormControl className={className} error={isTouched && Boolean(error)} size={size} variant={variant}>
      <InputLabel id={`${id}-label`}>{unifiedLabel}</InputLabel>
      <Select
        color={color}
        data-testid={`${id}-select`}
        disabled={disabled || !options.length}
        error={isTouched && Boolean(error)}
        id={id}
        label={unifiedLabel}
        labelId={`${id}-label`}
        MenuProps={
          multiple
            ? {
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
                getContentAnchorEl: null,
              }
            : undefined
        }
        multiple={multiple}
        onBlur={setTouched}
        onChange={handleChange(id)}
        renderValue={
          multiple
            ? selected => {
                if (!Array.isArray(selected)) return '';
                const chips = selected
                  .map(key => {
                    const option = options.find(o => o.key === key);
                    if (!option) return null;
                    const translationArray = ensureIsArray(option.label);
                    const labelText = safeT(t, ...translationArray, translationArray[1]);
                    return <Chip key={key} label={truncateLabel(labelText)} size='small' style={{ margin: '2px' }} />;
                  })
                  .filter(Boolean);
                return (
                  <Box display='flex' flexWrap='wrap'>
                    {chips}
                  </Box>
                );
              }
            : undefined
        }
        value={value}
      >
        {options.map(({ key, label: optionLabel, disabled: optionDisabled }) => {
          const translationArray = ensureIsArray(optionLabel);
          const labelText = safeT(t, ...translationArray, translationArray[1]);

          return (
            <MenuItem key={key} disabled={!!optionDisabled} value={key}>
              {multiple && <Checkbox checked={Array.isArray(value) && value.indexOf(key) > -1} />}
              <ListItemText primary={labelText} />
            </MenuItem>
          );
        })}
      </Select>
      <FormHelperText error>{isTouched && error}</FormHelperText>
    </FormControl>
  );
};

FormSelect.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string.isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  variant: PropTypes.string,
  color: PropTypes.string,
  formik: PropTypes.shape({
    touched: PropTypes.shape({}),
    errors: PropTypes.shape({}),
    handleChange: PropTypes.func,
    handleBlur: PropTypes.func,
    setFieldTouched: PropTypes.func,
    values: PropTypes.shape({}),
  }).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  required: PropTypes.bool,
  size: PropTypes.string,
  multiple: PropTypes.bool,
};

FormSelect.defaultProps = {
  variant: 'outlined',
  disabled: false,
  className: null,
  options: [],
  color: 'primary',
  required: false,
  size: 'medium',
  multiple: false,
};

export default FormSelect;
